import { FlowStep, FlowType } from 'src/frontend/constants/GettingStartedFlows';

import { DiagramService } from '@diagram/services/DiagramService';
import { LastUsedComponentInstanceService } from '@diagram/services/LastUsedComponentInstanceService';
import { gettingStartedTourState } from '@diagram/state/getting-started-tour';
import { SidebarTab } from '@diagram/state/sidebar';

import { DriverService } from 'services/drivers/DriverService';

export enum PowerFlowSimTourStep {
    HIGHLIGHT_ADD_SIDEBAR = 'power-flow-highlight-add-sidebar',
    CONNECT_COMPONENTS = 'power-flow-connect-components',
    SELECT_BATTERY = 'power-flow-select-battery',
    SET_BATTERY_VOLTAGE = 'power-flow-set-battery-voltage',
    SELECT_EV_CHARGER = 'power-flow-select-ev-charger',
    OPEN_SIMULATION_TAB = 'power-flow-open-simulation-tab',
    CONFIGURE_EV_CHARGER = 'power-flow-configure-ev-charger',
    OPEN_SIMULATIONS_NAV = 'power-flow-open-simulations-nav',
    CLICK_RUN_SIMULATION = 'power-flow-click-run-simulation',
    HIGHLIGHT_CREATE_FORM = 'power-flow-highlight-create-form',
    CLICK_VIEW = 'power-flow-click-view',
    FINISH = 'power-flow-finish',
}

const STEP_SEQUENCE: PowerFlowSimTourStep[] = [
    PowerFlowSimTourStep.HIGHLIGHT_ADD_SIDEBAR,
    PowerFlowSimTourStep.CONNECT_COMPONENTS,
    PowerFlowSimTourStep.SELECT_BATTERY,
    PowerFlowSimTourStep.SET_BATTERY_VOLTAGE,
    PowerFlowSimTourStep.SELECT_EV_CHARGER,
    PowerFlowSimTourStep.OPEN_SIMULATION_TAB,
    PowerFlowSimTourStep.CONFIGURE_EV_CHARGER,
    PowerFlowSimTourStep.OPEN_SIMULATIONS_NAV,
    PowerFlowSimTourStep.CLICK_RUN_SIMULATION,
    PowerFlowSimTourStep.HIGHLIGHT_CREATE_FORM,
    PowerFlowSimTourStep.CLICK_VIEW,
    PowerFlowSimTourStep.FINISH,
];

const PROXY_ATTR = 'data-power-flow-proxy';

const HIGHLIGHTS: Record<PowerFlowSimTourStep, FlowStep | null> = {
    [PowerFlowSimTourStep.HIGHLIGHT_ADD_SIDEBAR]: {
        selector: '[data-diagram-sidebar]',
        popover: {
            title: 'Drag a Battery and EV Charger onto the canvas',
            description: '<p>Drag a <strong>Battery</strong> and an <strong>EV Charger</strong> onto the diagram.</p>',
            side: 'left',
            align: 'start',
        },
    },
    [PowerFlowSimTourStep.CONNECT_COMPONENTS]: {
        selector: `[${PROXY_ATTR}]`,
        popover: {
            title: 'Connect Battery to EV Charger',
            description:
                '<p>Draw a connection between the <strong>Battery</strong> and the <strong>EV Charger</strong>.</p><p>Hover over the edge of one component and drag the blue dot to the other to create a connection.</p>',
            side: 'left',
            align: 'center',
        },
    },
    [PowerFlowSimTourStep.SELECT_BATTERY]: {
        selector: `[${PROXY_ATTR}]`,
        popover: {
            title: 'Select the Battery',
            description: '<p>Click on the <strong>Battery</strong> component on your diagram to select it.</p>',
            side: 'bottom',
            align: 'center',
        },
    },
    [PowerFlowSimTourStep.SET_BATTERY_VOLTAGE]: {
        selector: '[data-diagram-sidebar]',
        popover: {
            title: 'Set Battery voltage',
            description:
                '<p>Set the battery voltage, e.g. <strong>700 V</strong>.</p><p>This defines the operating voltage for your power flow simulation.</p><p>Click <strong>Next</strong> to continue.</p>',
            side: 'left',
            align: 'center',
        },
        showNextButton: true,
    },
    [PowerFlowSimTourStep.SELECT_EV_CHARGER]: {
        selector: `[${PROXY_ATTR}]`,
        popover: {
            title: 'Select the EV Charger',
            description: '<p>Click on the <strong>EV Charger</strong> component on your diagram to select it.</p>',
            side: 'bottom',
            align: 'center',
        },
    },
    [PowerFlowSimTourStep.OPEN_SIMULATION_TAB]: {
        selector: `[data-sidebar-tab="${SidebarTab.SIMULATION}"]`,
        popover: {
            title: 'Open the Simulation tab',
            description:
                '<p>Click the <strong>Simulation</strong> tab to configure how the EV Charger behaves during simulation.</p>',
            side: 'bottom',
            align: 'center',
        },
        popoverOffset: 10,
    },
    [PowerFlowSimTourStep.CONFIGURE_EV_CHARGER]: {
        selector: '[data-diagram-sidebar]',
        popover: {
            title: 'Configure the EV Charger',
            description:
                '<span>Control method: <strong>Constant Power</strong></span>' +
                '<br/>' +
                '<span>Power: <strong>35 kW</strong></span>' +
                '<p>Click <strong>Next</strong> to continue.</p>',
            side: 'left',
            align: 'center',
        },
        showNextButton: true,
    },
    [PowerFlowSimTourStep.OPEN_SIMULATIONS_NAV]: {
        selector: '[data-sidebar-type="simulations"]',
        popover: {
            title: 'Open the Simulations panel',
            description: '<p>Click the <strong>Power Flow Simulations</strong> icon in the sidebar navigation.</p>',
            side: 'left',
            align: 'center',
        },
    },
    [PowerFlowSimTourStep.CLICK_RUN_SIMULATION]: {
        selector: '[data-tour-power-flow-run]',
        popover: {
            title: 'Run a simulation',
            description: '<p>Click <strong>Run simulation</strong> to configure and run a power flow simulation.</p>',
            side: 'left',
            align: 'center',
        },
    },
    [PowerFlowSimTourStep.HIGHLIGHT_CREATE_FORM]: {
        selector: '[data-tour-power-flow-create-form]',
        popover: {
            title: 'Create your simulation',
            description:
                '<p>Give your simulation a name, choose <strong>Snapshot</strong> and click <strong>Create simulation</strong> to run it.</p><p>This will simulate the power flow between the Battery and EV Charger.</p>',
            side: 'left',
            align: 'center',
        },
    },
    [PowerFlowSimTourStep.CLICK_VIEW]: {
        selector: '[data-tour-power-flow-view]',
        popover: {
            title: 'Simulation complete!',
            description:
                '<p>Your power flow simulation is done. Click <strong>View</strong> to see the results on your diagram.</p>',
            side: 'bottom',
            align: 'center',
        },
    },
    [PowerFlowSimTourStep.FINISH]: {
        selector: `[${PROXY_ATTR}]`,
        popover: {
            title: 'Power flow simulation complete!',
            description:
                '<p>Your Battery is now powering the EV Charger. See the current values on the diagram connections.</p>' +
                '<h3>Learn more</h3>' +
                '<p>👉 <a href="https://directenergypartners.com/projects/WIA1V:S7HCF:KyWbO/editor" target="_blank">View more examples and step-by-step instructions in our <strong>reference design</strong></a></p>' +
                '<p>👉 <a href="https://www.youtube.com/watch?v=_xpgcHbKvCo" target="_blank">Watch the <strong>video tutorial</strong> on how to run a power flow simulation</a></p>' +
                '<h3>More power flow features</h3>' +
                '<p>👉 <a href="/blog/simulation-profiles" target="_blank">Model power generation and consumption change over time</a></p>' +
                '<p>👉 <a href="/blog/electric-vehicle-ev-pv-solar-droop-control-curtailment-simulation" target="_blank">Master droop control</a></p>' +
                '<p>👉 <a href="/blog/unbalanced-ac-dc-power-flow" target="_blank">Simulate unbalanced three-phase AC systems and mixed AC/DC systems</a></p>',

            side: 'bottom',
            align: 'center',
        },
        isLastStep: true,
    },
};

const PowerFlowSimDriverService = {
    start: (initStep: PowerFlowSimTourStep = PowerFlowSimTourStep.HIGHLIGHT_ADD_SIDEBAR) => {
        LastUsedComponentInstanceService.add('charger');
        LastUsedComponentInstanceService.add('battery');

        DriverService.request(FlowType.POWER_FLOW_SIMULATIONS);

        DriverService.setStep(FlowType.POWER_FLOW_SIMULATIONS, initStep);
    },

    advance: () => {
        DriverService.advance(FlowType.POWER_FLOW_SIMULATIONS, STEP_SEQUENCE);
    },

    showHighlight: (step: PowerFlowSimTourStep) => {
        const highlight = HIGHLIGHTS[step];

        if (!highlight) {
            return null;
        }

        let cleanupHighlight: (() => void) | null = null;

        const { componentInstances } = DiagramService.get();

        const tourBatteryId = gettingStartedTourState.tourComponentInstanceIds.find(
            (id) => componentInstances[id] && componentInstances[id].componentType === 'battery',
        );
        const tourChargerId = gettingStartedTourState.tourComponentInstanceIds.find(
            (id) => componentInstances[id] && componentInstances[id].componentType === 'charger',
        );

        if (step === PowerFlowSimTourStep.CONNECT_COMPONENTS || step === PowerFlowSimTourStep.FINISH) {
            cleanupHighlight = DriverService.addComponentIntancesHighlightBox(
                [tourBatteryId, tourChargerId].filter(Boolean) as string[],
                {
                    dataName: PROXY_ATTR,
                },
            );
        }

        if (step === PowerFlowSimTourStep.SELECT_BATTERY) {
            cleanupHighlight = DriverService.addComponentIntancesHighlightBox(
                [tourBatteryId].filter(Boolean) as string[],
                {
                    dataName: PROXY_ATTR,
                },
            );
        }

        if (step === PowerFlowSimTourStep.SELECT_EV_CHARGER) {
            cleanupHighlight = DriverService.addComponentIntancesHighlightBox(
                [tourChargerId].filter(Boolean) as string[],
                {
                    dataName: PROXY_ATTR,
                },
            );
        }

        return DriverService.showHighlight(FlowType.POWER_FLOW_SIMULATIONS, highlight, {
            onAdvance: () => PowerFlowSimDriverService.advance(),
        }).then((cleanup) => {
            return () => {
                cleanup();
                cleanupHighlight?.();
            };
        });
    },

    hideOnComponentDrag: () => {
        return DriverService.hideOnDrag(() =>
            PowerFlowSimDriverService.showHighlight(PowerFlowSimTourStep.HIGHLIGHT_ADD_SIDEBAR),
        );
    },
};

export { PowerFlowSimDriverService };
